import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardMedia, CardContent,
  Button, Chip, TextField, FormControl, InputLabel, Select, MenuItem,
  Pagination, Skeleton, InputAdornment, Container, Paper,
  Drawer, IconButton, useMediaQuery, useTheme, Divider,
  List, ListItem, ListItemButton, ListItemText, Collapse,
  ToggleButton, ToggleButtonGroup, Breadcrumbs, Link
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { HotSearches } from '../components/Recommendations';
import useBehaviorTracking from '../hooks/useBehaviorTracking';

// 修復圖片 URL（處理雙斜線問題）
const fixImageUrl = (url) => {
  if (!url) return 'https://via.placeholder.com/300x300?text=商品圖片';
  // 修復雙斜線問題：https://www.ezp.com.tw//images -> https://www.ezp.com.tw/images
  return url.replace(/([^:]\/)\/+/g, '$1');
};

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [categoryOpen, setCategoryOpen] = useState(true);
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const { trackSearch, trackCategory } = useBehaviorTracking();
  const lastSearchRef = useRef('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',  // 支援名稱或 ID
    sort: searchParams.get('sort') || 'created_at',
    page: parseInt(searchParams.get('page')) || 1
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  // 監聽 URL 參數變更，同步更新 filters（處理外部導航，如 Header 搜尋）
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    const urlCategory = searchParams.get('category') || '';
    const urlSort = searchParams.get('sort') || 'created_at';
    const urlPage = parseInt(searchParams.get('page')) || 1;

    // 只在 URL 參數與當前 filters 不同時才更新
    if (urlSearch !== filters.search ||
        urlCategory !== filters.category ||
        urlSort !== filters.sort ||
        urlPage !== filters.page) {
      setFilters({
        search: urlSearch,
        category: urlCategory,
        sort: urlSort,
        page: urlPage
      });
    }
  }, [searchParams]);

  useEffect(() => {
    fetchProducts();
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.category) params.set('category', filters.category);
    if (filters.sort !== 'created_at') params.set('sort', filters.sort);
    if (filters.page > 1) params.set('page', filters.page);
    setSearchParams(params, { replace: true });
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/products/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('取得分類失敗:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {
        page: filters.page,
        limit: 20,
        sort: filters.sort,
        order: filters.sort === 'sale_price' ? 'ASC' : 'DESC'
      };
      if (filters.search) params.search = filters.search;
      if (filters.category) params.category = filters.category;  // 支援名稱或 ID

      const response = await api.get('/products', { params });
      setProducts(response.data.items);
      setPagination(response.data.pagination);

      // GA4: view_item_list 事件
      if (response.data.items && response.data.items.length > 0) {
        const listName = filters.search
          ? `搜尋結果: ${filters.search}`
          : filters.category || '全部商品';
        const listId = filters.search
          ? `search_${filters.search}`
          : filters.category ? `category_${filters.category}` : 'all_products';

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce data
        window.dataLayer.push({
          event: 'view_item_list',
          ecommerce: {
            item_list_id: listId,
            item_list_name: listName,
            items: response.data.items.slice(0, 20).map((product, index) => ({
              item_id: product.sku || product.id.toString(),
              item_name: product.name,
              item_category: product.category_name || '',
              item_brand: product.brand || '優爾商城',
              price: product.sale_price,
              discount: product.original_price - product.sale_price,
              index: index,
              quantity: 1
            }))
          }
        });

        // Facebook Pixel: ViewCategory
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'ViewCategory', {
            content_category: listName,
            content_type: 'product_group',
            contents: response.data.items.slice(0, 10).map(p => ({
              id: p.sku || p.id.toString(),
              quantity: 1
            }))
          });
        }
      }

      if (filters.search && filters.search !== lastSearchRef.current) {
        trackSearch(filters.search, response.data.pagination?.total || 0);
        lastSearchRef.current = filters.search;

        // Facebook Pixel: Search
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Search', {
            search_string: filters.search,
            content_category: filters.category || '全部商品',
            contents: response.data.items.slice(0, 10).map(p => ({
              id: p.sku || p.id.toString(),
              quantity: 1
            })),
            content_type: 'product'
          });
        }
      }

      if (filters.category) {
        trackCategory(filters.category);
      }
    } catch (error) {
      console.error('取得商品失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleHotSearch = (keyword) => {
    // 點擊熱門搜尋時，清除分類篩選以搜尋全部商品
    setFilters(prev => ({ ...prev, search: keyword, category: '', page: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  const calculateDiscount = (original, sale) => {
    if (!original || original <= 0 || !sale) return 0;
    const discount = Math.round((1 - sale / original) * 100);
    return isNaN(discount) || !isFinite(discount) ? 0 : discount;
  };

  const getCategoryName = () => {
    if (filters.category) {
      // 如果是數字則用 ID 查找，否則直接用名稱
      const isNumeric = !isNaN(parseInt(filters.category));
      if (isNumeric) {
        const cat = categories.find(c => c.id === parseInt(filters.category));
        return cat?.name || '商品分類';
      }
      return filters.category;  // 直接返回名稱
    }
    return '全部商品';
  };

  // 判斷分類是否被選中（支援 ID 或名稱比對）
  const isCategorySelected = (cat) => {
    if (!filters.category) return false;
    const isNumeric = !isNaN(parseInt(filters.category));
    if (isNumeric) {
      return parseInt(filters.category) === cat.id;
    }
    return filters.category === cat.name;
  };

  // 側邊篩選器內容
  const FilterContent = () => (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        商品分類
      </Typography>
      <List disablePadding>
        <ListItemButton
          selected={!filters.category}
          onClick={() => {
            setFilters(prev => ({ ...prev, category: '', page: 1 }));
            if (isMobile) setFilterDrawerOpen(false);
          }}
          sx={{ borderRadius: 1, mb: 0.5 }}
        >
          <ListItemText primary="全部商品" />
        </ListItemButton>
        {categories.map(cat => (
          <ListItemButton
            key={cat.id}
            selected={isCategorySelected(cat)}
            onClick={() => {
              // 使用名稱以保持一致性
              setFilters(prev => ({ ...prev, category: cat.name, page: 1 }));
              if (isMobile) setFilterDrawerOpen(false);
            }}
            sx={{ borderRadius: 1, mb: 0.5 }}
          >
            <ListItemText primary={cat.name} />
            <Typography variant="caption" color="text.secondary">
              {cat.count || ''}
            </Typography>
          </ListItemButton>
        ))}
      </List>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        排序方式
      </Typography>
      <List disablePadding>
        {[
          { value: 'created_at', label: '最新上架' },
          { value: 'sale_price', label: '價格最低' },
          { value: 'sold_count', label: '銷量最高' },
          { value: 'discount', label: '折扣最多' }
        ].map(item => (
          <ListItemButton
            key={item.value}
            selected={filters.sort === item.value}
            onClick={() => {
              setFilters(prev => ({ ...prev, sort: item.value, page: 1 }));
              if (isMobile) setFilterDrawerOpen(false);
            }}
            sx={{ borderRadius: 1, mb: 0.5 }}
          >
            <ListItemText primary={item.label} />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  // GA4: select_item 事件
  const trackSelectItem = (product, index = 0) => {
    const listName = filters.search
      ? `搜尋結果: ${filters.search}`
      : filters.category || '全部商品';
    const listId = filters.search
      ? `search_${filters.search}`
      : filters.category ? `category_${filters.category}` : 'all_products';

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'select_item',
      ecommerce: {
        item_list_id: listId,
        item_list_name: listName,
        items: [{
          item_id: product.sku || product.id.toString(),
          item_name: product.name,
          item_category: product.category_name || '',
          item_brand: product.brand || '優爾商城',
          price: product.sale_price,
          discount: product.original_price - product.sale_price,
          index: index,
          quantity: 1
        }]
      }
    });
  };

  // 商品卡片
  const ProductCard = ({ product, index }) => {
    const handleProductClick = () => {
      trackSelectItem(product, index);
      navigate(`/products/${product.seo_url || product.id}`);
    };

    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: viewMode === 'list' ? 'row' : 'column',
          bgcolor: 'white',
          borderRadius: 2,
          border: '1px solid #eee',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-8px) scale(1.02)',
            boxShadow: '0 16px 40px rgba(0,0,0,0.15)',
            borderColor: '#1769ff',
            '& .product-image': {
              transform: 'scale(1.08)'
            },
            '& .add-cart-btn': {
              bgcolor: '#ff4444',
              transform: 'scale(1.02)'
            }
          }
        }}
      >
        {/* 商品圖片區 */}
        <Box
          sx={{
            position: 'relative',
            width: viewMode === 'list' ? 200 : '100%',
            minWidth: viewMode === 'list' ? 200 : 'auto',
            pt: viewMode === 'list' ? 0 : '100%',
            height: viewMode === 'list' ? 200 : 'auto',
            bgcolor: '#f9f9f9',
            cursor: 'pointer'
          }}
          onClick={handleProductClick}
        >
          {/* 庫存警示 */}
          {product.quantity <= 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: '#9e9e9e',
                color: 'white',
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: '0.7rem',
                zIndex: 1
              }}
            >
              已售完
            </Box>
          )}

          <CardMedia
            component="img"
            image={fixImageUrl(product.image_url)}
            alt={product.name}
            loading="lazy"
            className="product-image"
            sx={{
              position: viewMode === 'list' ? 'relative' : 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              p: 2,
              transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          />
        </Box>

        {/* 商品資訊 */}
        <CardContent sx={{ flexGrow: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
          {/* 分類標籤 */}
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            {product.category_name}
          </Typography>

          {/* 商品名稱 */}
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              minHeight: 40,
              lineHeight: 1.4,
              color: '#333',
              cursor: 'pointer',
              mb: 1,
              '&:hover': { color: '#1769ff' }
            }}
            onClick={handleProductClick}
          >
            {product.name}
          </Typography>

          <Box sx={{ mt: 'auto' }}>
            {/* 價格區 - 只顯示售價 */}
            <Typography
              variant="h6"
              sx={{ color: '#e53935', fontWeight: 'bold', fontSize: '1.25rem' }}
            >
              ${product.sale_price?.toLocaleString()}
            </Typography>

            {/* 配送標籤 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              <Chip
                icon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
                label="快速到貨"
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  bgcolor: '#e3f2fd',
                  color: '#1769ff',
                  '& .MuiChip-icon': { color: '#1769ff' }
                }}
              />
            </Box>
          </Box>

          {/* 加入購物車按鈕 */}
          <Button
            fullWidth
            variant="contained"
            startIcon={<ShoppingCartIcon />}
            onClick={() => addToCart(product.id)}
            disabled={product.quantity <= 0}
            className="add-cart-btn"
            sx={{
              mt: 2,
              bgcolor: product.quantity > 0 ? '#1769ff' : '#ccc',
              '&:hover': { bgcolor: product.quantity > 0 ? '#ff4444' : '#ccc' },
              textTransform: 'none',
              fontWeight: 'bold',
              transition: 'all 0.3s ease'
            }}
          >
            {product.quantity > 0 ? '加入購物車' : '已售完'}
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ py: 2, px: { xs: 1, sm: 2, md: 3, lg: 4 } }}>
        {/* 麵包屑導航 */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            color="inherit"
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            首頁
          </Link>
          <Typography color="text.primary">{getCategoryName()}</Typography>
        </Breadcrumbs>

        {/* 搜尋結果標題 */}
        {filters.search && (
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6">
              搜尋「<span style={{ color: '#e53935' }}>{filters.search}</span>」的結果
            </Typography>
            <Typography variant="body2" color="text.secondary">
              共找到 {pagination.total || 0} 件商品
            </Typography>
          </Paper>
        )}

        <Grid container spacing={2}>
          {/* 左側篩選器 - 桌面版 */}
          {!isMobile && (
            <Grid item md={2} lg={2}>
              <Paper sx={{ borderRadius: 2, position: 'sticky', top: 100 }}>
                <FilterContent />
              </Paper>
            </Grid>
          )}

          {/* 商品區 */}
          <Grid item xs={12} md={10} lg={10}>
            {/* 工具列 */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {/* 手機版篩選按鈕 */}
                {isMobile && (
                  <Button
                    startIcon={<FilterListIcon />}
                    onClick={() => setFilterDrawerOpen(true)}
                    variant="outlined"
                  >
                    篩選
                  </Button>
                )}

                {/* 搜尋框 */}
                <TextField
                  size="small"
                  placeholder="搜尋商品..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#999' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 200,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#f5f5f5'
                    }
                  }}
                />

                {/* 排序選擇 - 桌面版在這裡顯示 */}
                {!isMobile && (
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <Select
                      value={filters.sort}
                      onChange={(e) => setFilters(prev => ({ ...prev, sort: e.target.value, page: 1 }))}
                      displayEmpty
                    >
                      <MenuItem value="created_at">最新上架</MenuItem>
                      <MenuItem value="sale_price">價格最低</MenuItem>
                      <MenuItem value="sold_count">銷量最高</MenuItem>
                      <MenuItem value="discount">折扣最多</MenuItem>
                    </Select>
                  </FormControl>
                )}

                {/* 視圖切換 */}
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, v) => v && setViewMode(v)}
                  size="small"
                >
                  <ToggleButton value="grid">
                    <GridViewIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="list">
                    <ViewListIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>

                {/* 商品數量 */}
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                  共 {pagination.total || 0} 件商品
                </Typography>
              </Box>
            </Paper>

            {/* 熱門搜尋 */}
            <Box sx={{ mb: 3 }}>
              <HotSearches limit={10} onSearch={handleHotSearch} />
            </Box>

            {/* 商品列表 */}
            <Grid container spacing={2}>
              {loading
                ? Array(10).fill(0).map((_, i) => (
                    <Grid item xs={6} sm={viewMode === 'list' ? 12 : 4} md={viewMode === 'list' ? 12 : 3} lg={viewMode === 'list' ? 12 : 3} xl={viewMode === 'list' ? 12 : 2} key={i}>
                      <Card sx={{ borderRadius: 2 }}>
                        <Skeleton variant="rectangular" sx={{ pt: viewMode === 'list' ? '50%' : '100%' }} />
                        <CardContent>
                          <Skeleton width="80%" />
                          <Skeleton width="60%" />
                          <Skeleton width="40%" />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                : products.map((product, index) => (
                    <Grid
                      item
                      xs={viewMode === 'list' ? 12 : 6}
                      sm={viewMode === 'list' ? 12 : 4}
                      md={viewMode === 'list' ? 12 : 3}
                      lg={viewMode === 'list' ? 12 : 3}
                      xl={viewMode === 'list' ? 12 : 2}
                      key={product.id}
                    >
                      <ProductCard product={product} index={index} />
                    </Grid>
                  ))
              }
            </Grid>

            {/* 無商品提示 */}
            {!loading && products.length === 0 && (
              <Paper sx={{ textAlign: 'center', py: 8, borderRadius: 2 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  找不到符合條件的商品
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setFilters({ search: '', category: '', sort: 'created_at', page: 1 })}
                  sx={{ mt: 2 }}
                >
                  清除篩選條件
                </Button>
              </Paper>
            )}

            {/* 分頁 */}
            {pagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={pagination.totalPages}
                  page={filters.page}
                  onChange={(e, page) => setFilters(prev => ({ ...prev, page }))}
                  color="primary"
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      bgcolor: 'white'
                    }
                  }}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* 手機版篩選抽屜 */}
      <Drawer
        anchor="left"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{ sx: { width: 280 } }}
      >
        <Box sx={{ bgcolor: '#1769ff', color: 'white', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight="bold">
            篩選條件
          </Typography>
          <IconButton onClick={() => setFilterDrawerOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <FilterContent />
      </Drawer>
    </Box>
  );
};

export default ProductList;
